<template>
  <button
    @click="downloadChart"
    :disabled="!isChartReady"
    class="flex items-center justify-center p-2 rounded-md transition-colors duration-200"
    :class="isChartReady 
      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300' 
      : 'bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed'"
    :title="`Download ${chartName} as PNG`"
  >
    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
    </svg>
  </button>
</template>

<script>
import html2canvas from 'html2canvas'

export default {
  name: 'ChartDownloadButton',
  inject: ['globalState'],
  props: {
    // Chart instance reference (optional - can be null during initialization)
    chartInstance: {
      type: Object,
      required: false,
      default: null
    },
    // Chart name for filename (required)
    chartName: {
      type: String,
      required: true
    },
    // Whether chart is ready for download
    isChartReady: {
      type: Boolean,
      default: false
    },
    // Custom filename prefix (optional)
    filenamePrefix: {
      type: String,
      default: ''
    },
    // Whether to add white background (default: true)
    addWhiteBackground: {
      type: Boolean,
      default: true
    },
    // Container element to capture (includes title, legend, etc.)
    containerElement: {
      type: [HTMLElement, Object],
      required: false,
      default: null
    }
  },
  methods: {
    async downloadChart() {
      if (!this.isChartReady) {
        console.warn('Chart not ready for download')
        return
      }
      
      try {
        // Generate filename with subid and timestamp
        const now = new Date()
        const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-')
        
        // Get subid from injected global state
        const subid = this.globalState?.subid || ''
        const subidPart = subid ? `${subid}_` : ''
        const prefixPart = this.filenamePrefix ? `${this.filenamePrefix}_` : ''
        const filename = `${subidPart}${prefixPart}${this.chartName}-${timestamp}.png`
        
        // If container element is provided, use html2canvas to capture entire container
        if (this.containerElement) {
          const element = this.containerElement.$el || this.containerElement
          
          if (element) {
            // Hide the download button in the screenshot
            const downloadButtons = element.querySelectorAll('button')
            const originalDisplays = []
            downloadButtons.forEach(btn => {
              originalDisplays.push(btn.style.display)
              btn.style.display = 'none'
            })
            
            try {
              // Capture the entire container with html2canvas
              const canvas = await html2canvas(element, {
                backgroundColor: this.addWhiteBackground ? '#ffffff' : null,
                scale: 2, // Higher resolution
                logging: false,
                useCORS: true
              })
              
              // Restore button visibility
              downloadButtons.forEach((btn, index) => {
                btn.style.display = originalDisplays[index]
              })
              
              // Convert to PNG
              const url = canvas.toDataURL('image/png')
              
              // Create download link
              const link = document.createElement('a')
              link.download = filename
              link.href = url
              
              // Trigger download
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              
              console.log('Chart with title/legend downloaded successfully:', filename)
              return
            } catch (html2canvasError) {
              console.warn('html2canvas failed, falling back to canvas-only download:', html2canvasError)
              // Restore button visibility
              downloadButtons.forEach((btn, index) => {
                btn.style.display = originalDisplays[index]
              })
              // Fall through to canvas-only download
            }
          }
        }
        
        // Fallback: Use canvas-only download (original behavior)
        if (!this.chartInstance) {
          console.warn('No chart instance available for download')
          return
        }
        
        const canvas = this.chartInstance.canvas
        
        if (this.addWhiteBackground) {
          // Create a new canvas with white background
          const downloadCanvas = document.createElement('canvas')
          const ctx = downloadCanvas.getContext('2d')
          
          // Set the same dimensions as the original chart
          downloadCanvas.width = canvas.width
          downloadCanvas.height = canvas.height
          
          // Fill with white background
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height)
          
          // Draw the chart on top of the white background
          ctx.drawImage(canvas, 0, 0)
          
          // Convert to PNG with white background
          const url = downloadCanvas.toDataURL('image/png')
          
          // Create download link
          const link = document.createElement('a')
          link.download = filename
          link.href = url
          
          // Trigger download
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          console.log('Chart downloaded successfully with white background:', filename)
        } else {
          // Download without white background (transparent)
          const url = canvas.toDataURL('image/png')
          
          const link = document.createElement('a')
          link.download = filename
          link.href = url
          
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          console.log('Chart downloaded successfully:', filename)
        }
      } catch (error) {
        console.error('Error downloading chart:', error)
        alert('Failed to download chart. Please try again.')
      }
    }
  }
}
</script>
